import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import TaxFormModal from "./TaxFormModal";
import TaxViewModal from "./TaxViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "../components/useScreenPermissions";

import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

/* ----------------
   CurrencyMaster colors (adopted)
   ---------------- */
const COLORS = {
  CLOUD: "#FBFBFB", // page background
  BABY: "#D6EAF8", // header / table header / primary button tint
  DIVIDER: "#E6EEF2",
  BORDER: "rgba(226,232,240,1)", // card & row divider
  BABY1: "#AAD4F1", // alternate baby blue used in CurrencyMaster
};

const TaxMaster = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const [showView, setShowView] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [taxType, setTaxType] = useState("All");
  const [status, setStatus] = useState("All");
  const [isDefault, setIsDefault] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // pageTheme to pass to Sidebar so it matches CurrencyMaster look
  const pageTheme = {
    sidebarBg: "#ffffff",
    activeBg: COLORS.BABY1,
    activeText: "#000000",
    profileBg: COLORS.BABY1,
  };

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchTaxes();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchTaxes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/taxes`);
      const data = await res.json();
      setTaxes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setTaxes([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });

    if (!Array.isArray(taxes)) return;
    const sorted = [...taxes].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;

      if (key === "TaxCode") {
        const aNum = parseInt((a[key] || "").split("-")[1] || "0");
        const bNum = parseInt((b[key] || "").split("-")[1] || "0");
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      if (key === "CreatedDate") {
        return direction === "asc" ? new Date(a[key]) - new Date(b[key]) : new Date(b[key]) - new Date(a[key]);
      }
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setTaxes(sorted);
  };

 const handleLogout = () => {
    navigate('/login');
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" />;
    return sortConfig.direction === "asc" ? <FaSortUp className="ml-1 w-3 h-3" /> : <FaSortDown className="ml-1 w-3 h-3" />;
  };

  const handleView = (tax) => {
    setSelectedTax(tax);
    setShowView(true);
  };

  const handleEdit = (tax) => {
    setSelectedTax(tax);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (tax) => {
    if (!window.confirm('Are you sure you want to deactivate this tax?')) return;
    
    try {
      const response = await fetch(`${API_URL}/taxes/${tax.TaxCode}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate tax');
      }
      
      fetchTaxes();
    } catch (error) {
      console.error('Error deactivating tax:', error);
      alert(error.message || 'An error occurred while deactivating the tax');
    }
  };
  const handleReactivate = async (tax) => {
    if (!window.confirm('Are you sure you want to reactivate this tax?')) return;
    
    try {
      const response = await fetch(`${API_URL}/taxes/${tax.TaxCode}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate tax');
      }
      
      fetchTaxes();
    } catch (error) {
      console.error('Error reactivating tax:', error);
      alert(error.message || 'An error occurred while reactivating the tax');
    }
  };

  const handleDownloadCSV = () => {
    const headers = ["TaxCode", "TaxType", "TaxName", "Description", "Rate", "Country", "IsDefault", "Status"];
    const rows = taxes.map((t) => [t.TaxCode, t.TaxType, t.TaxName, t.Description, t.Rate, t.Country, t.IsDefault ? "Yes" : "No", t.Status]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "taxes.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedTax(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedTax) {
        const response = await fetch(`${API_URL}/taxes/${selectedTax.TaxCode}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update tax");
        }
      } else {
        const response = await fetch(`${API_URL}/taxes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create tax");
        }
      }
      setShowForm(false);
      fetchTaxes();
    } catch (err) {
      console.error(err);
      alert(err.message || "An error occurred while saving the tax");
    } finally {
      setFormLoading(false);
    }
  };

  // Status colors — match CurrencyMaster style (solid)
  const getStatusColor = (status) => {
    const isActive = status === true || status === "Active" || status === 1 || status === "1";
    return isActive ? "bg-teal-50 text-teal-700 font-bold hover:bg-teal-100" : "bg-amber-50 text-amber-700 hover:bg-amber-200";
  };

  const getStatusText = (status) => {
    // Convert boolean, numeric or string status to text
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };
  const getDefaultText = (isDefault) => {
    // Convert boolean, numeric or string IsDefault to text
    if (isDefault === true || isDefault === 1 || isDefault === '1') return 'Yes';
    if (isDefault === false || isDefault === 0 || isDefault === '0') return 'No';
    return isDefault ? 'Yes' : 'No';
  };

  const getDefaultColor = (isDefault) => {
    const isDef = isDefault === true || isDefault === 1 || isDefault === "1";
    return isDef ? "bg-green-600 text-white" : "bg-red-600 text-white";
  };

  const filteredTaxes = taxes.filter((t) => {
    const matchesSearch =
      searchTerm === "" ||
      (t.TaxCode && t.TaxCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.TaxType && t.TaxType.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.TaxName && t.TaxName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.Description && t.Description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.Country && t.Country.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTaxType = taxType === "All" || t.TaxType === taxType;
    const matchesStatus =
      status === "All" ||
      (status === "Active" && getStatusText(t.Status) === "Active") ||
      (status === "Inactive" && getStatusText(t.Status) === "Inactive");
    const matchesIsDefault = isDefault === "All" || (isDefault === "Yes" && getDefaultText(t.IsDefault) === "Yes") || (isDefault === "No" && getDefaultText(t.IsDefault) === "No");
    return matchesSearch && matchesTaxType && matchesStatus && matchesIsDefault;
  });

  // pagination
  const totalPages = Math.ceil(filteredTaxes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const taxesForPage = filteredTaxes.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={currentPage === 1 ? { color: "#9CA3AF", cursor: "not-allowed" } : { color: "#2563eb" }}
        aria-label="Previous page"
      >
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      const isActive = currentPage === i;
      pages.push(
        <button
          key={i}
          className={`px-4 py-1 font-medium transition-all duration-200 ${isActive ? "" : "hover:bg-gray-100"}`}
          onClick={() => handlePageChange(i)}
          style={isActive ? { backgroundColor: COLORS.BABY1, color: "#0F172A" } : { color: "#2563eb" }}
          aria-current={isActive ? "page" : undefined}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        style={currentPage === totalPages || totalPages === 0 ? { color: "#9CA3AF", cursor: "not-allowed" } : { color: "#2563eb" }}
        aria-label="Next page"
      >
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );

    return pages;
  };

  // stats
  const totalTaxes = taxes.length;
  const activeTaxes = taxes.filter((t) => getStatusText(t.Status) === "Active").length;

  // permissions
  const canCreate = hasFullAccess || hasPermission("MRN_MASTER", "CanCreate");
  const canView = hasFullAccess || hasPermission("MRN_MASTER", "CanView");
  const canEdit = hasFullAccess || hasPermission("MRN_MASTER", "CanEdit");
  const canDelete = hasFullAccess || hasPermission("MRN_MASTER", "CanDelete");

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: COLORS.CLOUD }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto" style={{ borderTopColor: COLORS.BABY, borderBottomColor: COLORS.BABY }}></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.CLOUD }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={pageTheme} onLogout={handleLogout} />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* small scoped CSS to force a continuous divider across the entire row */}
        <style>{`
          table.custom-table { border-collapse: separate; border-spacing: 0; width: 100%; }
          table.custom-table thead th { background-clip: padding-box; }
          table.custom-table tbody tr { position: relative; }
          table.custom-table tbody tr::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            height: 1px;
            background: #CBD5E1;
            bottom: 0;
            pointer-events: none;
          }
          table.custom-table tbody td { border-bottom: none; }
          table.custom-table thead th.sticky { position: sticky; top: 0; z-index: 20; }
        `}</style>

        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 16 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Tax Master</h1>
              <p className="mt-1 text-base text-gray-600">Manage your tax rates and configurations</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="flex items-center gap-2 px-5 py-2 bg-white border font-medium transition-all duration-200 shadow-sm"
                onClick={handleDownloadCSV}
                style={{ borderColor: COLORS.BORDER, color: "#0F172A" }}
                aria-label="Export CSV"
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm"
                  style={{ backgroundColor: COLORS.BABY1, color: "#0F172A" }}
                >
                  <FaPlus className="w-4 h-4" />
                  Create New
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Total Taxes</p>
                <p className="text-2xl font-bold text-slate-900">{totalTaxes}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-slate-100 text-slate-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 01.5-.5h1a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5v-1zm5 0a.5.5 0 01.5-.5h1a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5v-1z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Active Taxes</p>
                <p className="text-2xl font-bold text-slate-900">{activeTaxes}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-green-50 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Default Taxes</p>
                <p className="text-2xl font-bold text-slate-900">{taxes.filter((t) => getDefaultText(t.IsDefault) === "Yes").length}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-yellow-50 text-yellow-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">This Month</p>
                <p className="text-2xl font-bold text-slate-900">
                  {taxes.filter((t) => {
                    const d = t.CreatedDate ? new Date(t.CreatedDate) : null;
                    if (!d) return false;
                    const now = new Date();
                    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-purple-50 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: "#6b7280" }}>What are you looking for?</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for tax code, name, type, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                  style={{ borderColor: COLORS.BORDER }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: "#6b7280" }}>Tax Type</label>
              <select
                value={taxType}
                onChange={(e) => setTaxType(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option>All</option>
                {/* derive types safely */}
                {[...new Set(taxes.map((t) => t.TaxType).filter(Boolean))].map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: "#6b7280" }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: "#6b7280" }}>Default</label>
              <select
                value={isDefault}
                onChange={(e) => setIsDefault(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option>All</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>

            <button
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm"
              onClick={() => setCurrentPage(1)}
              style={{ backgroundColor: COLORS.BABY1, color: "#0F172A" }}
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold text-slate-900">Tax Summary</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-500">{renderPaginationButtons()}</div>
            </div>
          </div>

          <div className="h-full overflow-x-auto">
            <table className="w-full custom-table" style={{}}>
              <thead className="text-xs font-bold uppercase text-gray-600" style={{ backgroundColor: COLORS.BABY, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                <tr>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("TaxCode")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Tax Code {getSortIcon("TaxCode")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("TaxType")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Tax Type {getSortIcon("TaxType")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("TaxName")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Tax Name {getSortIcon("TaxName")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("Rate")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Rate (%) {getSortIcon("Rate")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("Country")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Country {getSortIcon("Country")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("IsDefault")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Default {getSortIcon("IsDefault")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("Status")} style={{ backgroundColor: COLORS.BABY }}>
                    <div className="flex items-center uppercase">Status {getSortIcon("Status")}</div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-3 text-center text-xs font-semibold`} style={{ backgroundColor: COLORS.BABY }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500 font-semibold">Loading taxes...</td>
                  </tr>
                ) : filteredTaxes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500 font-semibold">No taxes found.</td>
                  </tr>
                ) : (
                  taxesForPage.map((tax, idx) => (
                    <tr key={tax.TaxCode || idx} className="transition-all duration-200 group hover:bg-slate-50" style={{ backgroundColor: "#ffffff" }}>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap uppercase">
                        <p className={`text-slate-900 transition-all duration-200 cursor-pointer ${!canView ? "pointer-events-none" : ""}`} onClick={() => canView && handleView(tax)}>{tax.TaxCode || "N/A"}</p>
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap font-semibold uppercase">{tax.TaxType || "N/A"}</td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap font-semibold uppercase">{tax.TaxName || "N/A"}</td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap text-gray-600">{tax.Rate ? `${tax.Rate}%` : "N/A"}</td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap text-gray-600 uppercase">{tax.Country || "N/A"}</td>
                      <td className="px-6 py-3 text-left text-sm">
                        <span className={`inline-block px-3 py-1 text-xs font-bold transition-all duration-200 uppercase ${getDefaultColor(tax.IsDefault)}`}>
                          {getDefaultText(tax.IsDefault)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-left text-sm">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold transition-all duration-200 uppercase rounded-full ${getStatusColor(tax.Status)}`}>
                          {getStatusText(tax.Status)}
                        </span>
                      </td>
                      <td className="px-6 py-3 flex gap-2 justify-center items-center">
                        {canView && (
                          <button title="View" className="p-2 rounded-none text-gray-500 hover:bg-slate-100 transition-all duration-150" onClick={() => handleView(tax)}>
                            <FaEye />
                          </button>
                        )}
                        {canEdit && (
                          <button title="Edit" className="p-2 rounded-none text-gray-500 hover:bg-slate-100 transition-all duration-150" onClick={() => handleEdit(tax)}>
                            <FaEdit />
                          </button>
                        )}
                        {getStatusText(tax.Status) === "Active" ? (
                          <button title="Deactivate" className="p-2 rounded-none hover:bg-red-50 text-red-500 transition-all duration-150" onClick={() => handleDeactivate(tax)}>
                            <FaBan />
                          </button>
                        ) : (
                          <button title="Reactivate" className="p-2 rounded-none hover:bg-green-50 text-green-500 transition-all duration-150" onClick={() => handleReactivate(tax)}>
                            <FaCheck />
                          </button>
                        )}
                        <button title="Download CSV" className="p-2 rounded-none hover:bg-slate-100 text-gray-500 transition-all duration-150" onClick={handleDownloadCSV}>
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

       {/* Modals */}
        <TaxFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedTax}
          isEdit={isEdit}
          loading={formLoading}
        />
        <TaxViewModal
          open={showView}
          onClose={() => setShowView(false)}
          tax={selectedTax}
        />
      </main>
    </div>
  );
};

export default TaxMaster;
